const STORAGE_KEY = 'completed-guest-checkout-uuids'

const readCompletedCheckoutUuids = () => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')
        return Array.isArray(stored) ? stored.filter(uuid => typeof uuid === 'string' && uuid) : []
    } catch (error) {
        return []
    }
}

export const markCheckoutCompleted = uuid => {
    if (!uuid) {
        return false
    }

    try {
        const completedUuids = readCompletedCheckoutUuids()
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...new Set([...completedUuids, uuid])]))
        return true
    } catch (error) {
        console.warn('Could not save completed guest checkout marker', {
            errorName: error.name
        })
        return false
    }
}

export const isCheckoutCompleted = uuid => {
    if (!uuid) {
        return false
    }

    return readCompletedCheckoutUuids().includes(uuid)
}

// The customer has already paid by the time this runs. Building the member session is worth a short
// wait - the success page then opens logged in - but a call that is merely slow must never hold a paid
// customer on the checkout progress bar, where the only thing they can conclude is that payment failed.
export const GUEST_LOGIN_TIMEOUT_MS = 10000

const GUEST_LOGIN_TIMED_OUT = 'timed_out'

const waitForLogin = (login, timeoutMs) => {
    let timer

    const timeout = new Promise(resolve => {
        timer = setTimeout(() => resolve(GUEST_LOGIN_TIMED_OUT), timeoutMs)
    })

    return Promise.race([login, timeout]).finally(() => clearTimeout(timer))
}

export const completeSuccessfulGuestCheckout = async ({ checkoutStore, timeoutMs = GUEST_LOGIN_TIMEOUT_MS }) => {
    const wasGuestCheckout = checkoutStore.HasPendingGuestLogin()

    if (!wasGuestCheckout) {
        return { wasGuestCheckout: false, loginResult: 'skipped' }
    }

    const login = Promise.resolve(checkoutStore.FinalizeGuestLoginAfterOrder())

    // The login keeps running after a timeout, and it may still finish the session in the background.
    // Its own rejection is already handled below, but the race stops listening once the timer wins, so
    // attach a second handler to keep a late rejection from surfacing as an unhandled one.
    login.catch(() => undefined)

    try {
        const loginResult = await waitForLogin(login, timeoutMs)

        if (loginResult === GUEST_LOGIN_TIMED_OUT) {
            console.warn('Guest login session initialization is taking too long, continuing to the success page')
        }

        return { wasGuestCheckout: true, loginResult }
    } catch (error) {
        return { wasGuestCheckout: true, loginResult: 'failed' }
    }
}

export const completeSuccessfulOrder = async ({ response, uuid, checkoutStore, finishOrderState, navigate, timeoutMs }) => {
    if (!response.success) {
        return false
    }

    await completeSuccessfulGuestCheckout({ checkoutStore, timeoutMs })

    finishOrderState()

    // Members too, not only guests: browser Back must not reach a paid checkout either way, and the
    // guard has nothing but this marker to tell a dead entry from a fresh one.
    markCheckoutCompleted(uuid)

    const successPath = response.gateway_ref_id
        ? `/payment-response/success?gateway_ref_id=${response.gateway_ref_id}`
        : `/payment-response/success?payment_id=${response.payment_id}`

    // Browser Back from the success page must land on Landing, never on a checkout step. Replacing
    // the payment entry alone is not enough: the entry under it is /checkout/shipping with no uuid
    // in the URL, so CompletedGuestCheckoutGuard cannot recognise it as finished. Put Landing in the
    // payment entry's place, then push the success page on top of it.
    navigate('/', { replace: true })
    navigate(successPath)
    return true
}
